/**
 * Every concrete (parameter-free) path this app's router actually renders.
 *
 * Why this list exists: the router ends with `<Route path="*" element={<Navigate to="/login" />} />`,
 * so a nav item pointing at a path no route matches does not 404 — it silently throws the
 * operator back to the login screen. When the Access Modules catalog is the source of truth
 * for the sidebar, a row may name a route this build has no component for (registered for a
 * newer frontend, or a `/plans/care-programs/:programId`-style template that is not a
 * navigable destination). Such a row must not become a nav item.
 *
 * Kept in sync with src/index.js by appRoutes.test.js, which fails if a new absolute route
 * is added there without being listed here. Parameterised routes are deliberately absent:
 * a `:param` template is not somewhere a user can be sent from the nav.
 */
export const APP_ROUTE_PATHS = [
  '/',
  '/login',
  '/logout',

  '/verify',
  '/search',
  '/checkins',
  '/checkins/facility-summary',
  '/admin/soft-deleted-checkins',
  '/admin/appointments',
  '/notifications',
  '/admin/campaigns',
  '/admin/notifications/broadcast',
  '/admin/email-campaigns',
  '/announcements',
  '/admin/announcements',
  '/admin/facility-notices',
  '/admin/form-settings',
  '/admin/notification-preferences',
  '/admin/settings/notifications',
  '/admin/settings/notification-system',
  '/admin/settings/sms-provider',
  '/admin/settings/claims-rollout',
  '/admin/settings/test-subjects',
  '/admin/facility-ratings',
  '/admin/help-content',
  '/admin/settings/facility-types',
  '/admin/plan-management',
  '/settings/enrollment-segments',
  '/admin/enrollment-segments',
  '/preauth',
  '/preauth-action',
  '/file-encounter',
  '/dash',
  '/rbac',
  '/platform/contexts',
  '/platform/identity-generation-settings',
  '/admin/onboarding-otp',
  '/dashboard',
  '/dashboard/preauth',
  '/dashboard/enrollees',

  '/claims-action',
  '/claims',
  '/claims/facility-summary',
  '/claims/pending-submissions',
  '/claims/filings',

  '/corporations',
  '/corporations/plan-requests',
  '/corporations/staff-uploads',
  '/corporations/enrollment-holds',
  '/corporations/enrollment-conflicts',
  '/corporations/users',

  '/facilities',
  '/facilities/suggestions',


  '/admin/integrations/partners',
  '/admin/integrations/saaspro',
  '/admin/tariffs',

  '/log',

  '/plans',
  '/plans/care-programs/utilization',
  '/admin/care-programs',
  '/admin/checkin-types',
];

const ROUTABLE_PATHS = new Set(APP_ROUTE_PATHS);

/**
 * The pathname part of a nav/catalog route: query string and hash dropped, trailing
 * slash removed. Catalog rows are hand-typed by operators, so both show up.
 */
export const routePathname = (path) => {
  const raw = String(path == null ? '' : path).trim();
  if (!raw) return '';
  const [withoutHash] = raw.split('#');
  const [pathname] = withoutHash.split('?');
  if (pathname.length > 1 && pathname.endsWith('/')) return pathname.replace(/\/+$/, '');
  return pathname;
};

/** True when this app has a component mounted at `path` (parameterised routes are not). */
export const isRoutablePath = (path) => {
  const pathname = routePathname(path);
  return Boolean(pathname) && ROUTABLE_PATHS.has(pathname);
};
